// src/components/pages/CheckoutService.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getColors } from '../../styles/colors';
import { 
  FaArrowLeft, FaCreditCard, FaSpinner, FaMapMarkerAlt,
  FaUser, FaPhone, FaSync, FaMoneyBillWave
} from 'react-icons/fa';
import { placeOrderAsync, fetchCartItemsAsync } from '../../redux/slices/cartSlice';
import apiService from '../../api/authApi';

const CheckoutService = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector((state) => state.theme?.mode || 'light');
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { items, totalPrice, totalItems, isLoading } = useSelector((state) => state.cart);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cod');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    const loadCart = async () => {
      try {
        console.log('🔄 Loading cart items on mount...');
        await dispatch(fetchCartItemsAsync()).unwrap();
        console.log('✅ Cart loaded successfully');
      } catch (error) {
        console.error('❌ Failed to load cart:', error);
      }
    };
    loadCart();

    const userData = user || JSON.parse(localStorage.getItem('user') || '{}');
    setFormData({
      name: userData.name || '',
      phone: userData.phone || '',
      address: userData.address || ''
    });

    console.log('📦 Redux cart items:', items);
    console.log('👤 User data:', userData);

  }, [user, navigate, dispatch]);

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + (parseFloat(item.price) * parseInt(item.quantity)), 0);
  };

  const cartTotal = totalPrice || calculateTotal();
  const grandTotal = cartTotal;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRefreshCart = async () => {
    setIsRefreshing(true);
    try {
      console.log('🔄 Refreshing cart...');
      const result = await dispatch(fetchCartItemsAsync()).unwrap();
      console.log('✅ Cart refreshed:', result);
      alert('Cart refreshed successfully! Items: ' + (result?.data?.length || 0));
    } catch (error) {
      console.error('❌ Failed to refresh cart:', error);
      alert('Failed to refresh cart. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  // ✅ Update user profile with address - NEW FUNCTION
  const updateUserProfileAddress = async (address, name, phone) => {
    try {
      console.log('📤 Updating user profile with address:', address);
      
      const userData = {
        name: name,
        phone: phone,
        address: address
      };
      
      const response = await apiService.updateUserProfile(userData);
      console.log('✅ Profile updated with address:', response);
      
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      storedUser.address = address;
      storedUser.name = name;
      storedUser.phone = phone;
      localStorage.setItem('user', JSON.stringify(storedUser));
      
      const authUser = JSON.parse(localStorage.getItem('tiffinUserLogin') || '{}');
      authUser.address = address;
      authUser.name = name;
      authUser.phone = phone;
      localStorage.setItem('tiffinUserLogin', JSON.stringify(authUser));
      
      window.dispatchEvent(new CustomEvent('profileUpdated', { 
        detail: { 
          name: name,
          phone: phone,
          address: address 
        } 
      }));
      window.dispatchEvent(new Event('storage'));
      
      return true;
    } catch (error) {
      console.error('❌ Failed to update profile address:', error);
      return false;
    }
  };

  const paymentOptions = [
    { value: 'cod', label: 'COD (cod)' },
    { value: 'COD', label: 'COD (COD)' },
    { value: 'cash', label: 'Cash (cash)' },
    { value: 'Cash on Delivery', label: 'Cash on Delivery' },
    { value: 'cash_on_delivery', label: 'cash_on_delivery' },
    { value: 'cashondelivery', label: 'cashondelivery' },
    { value: 'card', label: 'Card' },
    { value: 'online', label: 'Online' },
    { value: 'upi', label: 'UPI' },
  ];

  const handlePlaceOrder = async () => {
    if (!formData.name || !formData.phone || !formData.address) {
      alert('Please fill in all delivery details');
      return;
    }

    setIsSubmitting(true);
    
    try {
      try {
        console.log('🔄 Refreshing cart before placing order...');
        await dispatch(fetchCartItemsAsync()).unwrap();
        console.log('✅ Cart refreshed before order');
      } catch (refreshError) {
        console.warn('⚠️ Could not refresh cart:', refreshError);
      }
      
      let cartItems = items || [];
      console.log('📦 Cart items from Redux before order:', cartItems);
      
      if (cartItems.length === 0) {
        try {
          const stored = localStorage.getItem('cartItems');
          if (stored) {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed) && parsed.length > 0) {
              cartItems = parsed;
              console.log('📦 Using cart items from localStorage:', cartItems);
            }
          }
        } catch (e) {
          console.warn('Could not parse cart items from localStorage');
        }
      }
      
      if (!cartItems || cartItems.length === 0) {
        alert('Your cart is empty! Please add items before placing an order.');
        setIsSubmitting(false);
        return;
      }
      
      console.log('📦 Final cart items for order:', cartItems);
      
      const userId = user?.id || JSON.parse(localStorage.getItem('user') || '{}')?.id || 1;
      
      const formattedItems = cartItems.map(item => ({
        item_id: parseInt(item.item_id) || parseInt(item.id) || 0,
        quantity: parseInt(item.quantity) || 1,
        price: parseFloat(item.price) || 0,
        item_type: item.item_type || 'product',
        product_name: item.product_name || item.name || 'Item'
      }));
      
      console.log('📦 Formatted items:', formattedItems);
      
      const subtotal = formattedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const total = subtotal;
      
      const orderData = {
        user_id: parseInt(userId),
        payment_method: paymentMethod,
        customer_address: formData.address,
        customer_phone: formData.phone,
        customer_name: formData.name,
        items: formattedItems,
        subtotal: subtotal,
        total: total,
        delivery_charge: 0,
        latitude: 22.5726,
        longitude: 88.3639
      };
      
      console.log('📤 Placing order with payload:', JSON.stringify(orderData, null, 2));
      console.log('💳 Payment Method being sent:', paymentMethod);
      console.log('📊 Order total: ₹' + total.toFixed(2));
      
      // ✅ FIRST: Update user profile with the address
      console.log('📤 Updating user profile with address...');
      await updateUserProfileAddress(formData.address, formData.name, formData.phone);
      console.log('✅ Profile updated with address');
      
      // ✅ SECOND: Place the order
      const result = await dispatch(placeOrderAsync(orderData)).unwrap();
      console.log('✅ Order placed:', result);
      
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      userData.address = formData.address;
      userData.name = formData.name;
      userData.phone = formData.phone;
      localStorage.setItem('user', JSON.stringify(userData));
      
      localStorage.removeItem('cartItems');
      sessionStorage.removeItem('cartItems');
      
      const orderResult = result?.data || result;
      navigate('/order-success', { 
        state: { 
          orderId: orderResult?.order_id || orderResult?.id,
          orderNumber: orderResult?.order_number,
          totalAmount: orderResult?.total_amount || total,
          paymentMethod: 'Cash on Delivery'
        } 
      });
      
    } catch (error) {
      console.error('❌ Order failed:', error);
      
      let errorMsg = 'Failed to place order. Please try again.';
      if (typeof error === 'string') {
        errorMsg = error;
      } else if (error?.message) {
        errorMsg = error.message;
      } else if (error?.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error?.response?.data?.errors) {
        const errors = error.response.data.errors;
        errorMsg = Object.values(errors).flat().join(', ');
      }
      
      alert('❌ ' + errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated && !localStorage.getItem('token')) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <p className="text-lg" style={{ color: colors.text }}>Please login to checkout</p>
          <button 
            onClick={() => navigate('/login')}
            className="mt-4 px-6 py-2 rounded-full text-white"
            style={{ backgroundColor: colors.primary }}
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      <div className="relative pt-8 pb-6 px-4">
        <div className="container mx-auto">
          <button
            onClick={() => navigate('/view-cart')}
            className="flex items-center gap-2 mb-4 px-4 py-2 rounded-full transition-all hover:scale-105"
            style={{ 
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`,
              color: colors.text
            }}
          >
            <FaArrowLeft />
            <span>Back to Cart</span>
          </button>
          
          <h1 className="text-3xl font-bold" style={{ color: colors.text }}>
            Checkout
          </h1>
          <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>
            <FaMoneyBillWave className="inline mr-1" />
            Cash on Delivery
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-xl p-6" style={{ 
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`
            }}>
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: colors.text }}>
                <FaMapMarkerAlt style={{ color: colors.primary }} />
                Delivery Details
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full p-3 rounded-xl focus:outline-none"
                    style={{ 
                      backgroundColor: colors.inputBackground,
                      border: `1px solid ${colors.border}`,
                      color: colors.text
                    }}
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full p-3 rounded-xl focus:outline-none"
                    style={{ 
                      backgroundColor: colors.inputBackground,
                      border: `1px solid ${colors.border}`,
                      color: colors.text
                    }}
                    placeholder="Enter your phone number"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                    Delivery Address *
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full p-3 rounded-xl focus:outline-none resize-none"
                    style={{ 
                      backgroundColor: colors.inputBackground,
                      border: `1px solid ${colors.border}`,
                      color: colors.text
                    }}
                    placeholder="Enter your delivery address"
                  />
                  {/* ✅ Added note about auto-save */}
                  <p className="text-xs mt-1" style={{ color: colors.primary }}>
                    📍 This address will be automatically saved to your profile
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="rounded-xl p-6 sticky top-4" style={{ 
              backgroundColor: colors.card,
              border: `1px solid ${colors.border}`
            }}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold" style={{ color: colors.text }}>
                  Order Summary
                </h3>
                <button
                  onClick={handleRefreshCart}
                  disabled={isRefreshing}
                  className="text-sm px-3 py-1.5 rounded-lg transition-all hover:scale-105 flex items-center gap-2"
                  style={{ 
                    backgroundColor: colors.primary,
                    color: 'white',
                    opacity: isRefreshing ? 0.6 : 1
                  }}
                >
                  <FaSync className={isRefreshing ? 'animate-spin' : ''} />
                  {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
              
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {isLoading ? (
                  <div className="text-center py-4" style={{ color: colors.textSecondary }}>
                    Loading cart items...
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-4" style={{ color: colors.textSecondary }}>
                    <p>Your cart is empty</p>
                    <button
                      onClick={() => navigate('/products')}
                      className="mt-2 text-sm underline"
                      style={{ color: colors.primary }}
                    >
                      Browse Products
                    </button>
                  </div>
                ) : (
                  items.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm" style={{ color: colors.textSecondary }}>
                      <span>{item.product_name || 'Item'} × {item.quantity}</span>
                      <span>₹{(parseFloat(item.price) * parseInt(item.quantity)).toFixed(2)}</span>
                    </div>
                  ))
                )}
              </div>
              
              <div className="border-t pt-3 mt-3 space-y-2" style={{ borderColor: colors.border }}>
                <div className="flex justify-between text-sm" style={{ color: colors.textSecondary }}>
                  <span>Subtotal ({totalItems || items.length} items)</span>
                  <span>₹{cartTotal.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between text-sm" style={{ color: colors.textSecondary }}>
                  <span>Delivery Fee</span>
                  <span className="text-green-500">Free</span>
                </div>
                
                <div className="border-t pt-2 mt-2" style={{ borderColor: colors.border }}>
                  <div className="flex justify-between">
                    <span className="font-bold" style={{ color: colors.text }}>Total</span>
                    <span className="text-xl font-bold" style={{ color: colors.primary }}>
                      ₹{grandTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* ✅ Payment Method Dropdown */}
              <div className="mt-3">
                <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
                  Payment Method
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full p-2.5 rounded-xl focus:outline-none"
                  style={{ 
                    backgroundColor: colors.inputBackground,
                    border: `1px solid ${colors.border}`,
                    color: colors.text
                  }}
                >
                  {paymentOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>
                  Try different payment methods to find which one works
                </p>
              </div>

              <div className="mt-3 p-3 rounded-lg" style={{ 
                backgroundColor: '#22C55E15',
                border: `1px solid #22C55E30`
              }}>
                <div className="flex items-center gap-2 text-sm" style={{ color: '#22C55E' }}>
                  <FaMoneyBillWave />
                  <span className="font-medium">Payment: {paymentMethod}</span>
                </div>
                <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>
                  {paymentMethod === 'cod' || paymentMethod === 'COD' || paymentMethod === 'cash' 
                    ? 'You pay when you receive your order' 
                    : 'Online payment method'}
                </p>
              </div>

              <button
                onClick={handlePlaceOrder}
                disabled={isSubmitting || items.length === 0}
                className="w-full mt-4 py-3.5 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  backgroundColor: items.length === 0 ? colors.textSecondary : colors.primary,
                  boxShadow: items.length === 0 ? 'none' : `0 4px 20px ${colors.primary}40`
                }}
              >
                {isSubmitting ? (
                  <><FaSpinner className="animate-spin" /> Placing Order...</>
                ) : (
                  <><FaMoneyBillWave /> Place Order</>
                )}
              </button>
              
              {items.length === 0 && (
                <p className="text-center text-xs mt-2" style={{ color: colors.textSecondary }}>
                  Add items to your cart to place an order
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutService;